/* jshint esversion: 6 */
import React from 'react';
import PropTypes from 'prop-types';
import Content from 'app/components/common/Content.jsx';
import EditInputHeader from 'app/components/Headers/EditInputHeader.jsx';
import FormUtilityMixin from 'app/mixins/FormUtilityMixin.js';
import DeleteModal from 'app/components/common/DeleteModal.jsx';
import ConfirmModal from 'app/components/common/ConfirmModal.jsx';
import Utilities from 'app/utility/Utilities.js';
import * as _ from 'underscore';
import { Outlet } from 'react-router-dom';
import ComponentWithContext from 'app/components/Routers/ComponentWithContext.jsx';
import PersistData from 'app/mixins/PersistData.jsx';
import { compose } from 'redux';
import { History, Stethoscope } from 'lucide-react';
import FeedSettings from '@/shadcn/components/settings/feed-settings.tsx';
import SourceSettings from '@/shadcn/components/settings/complex/source-settings.tsx';
import {
	buildFeedSections,
	feedSourceType,
	feedSubtype,
	FEED_COMPOSITE_PATHS,
	FEED_ERROR_KEYS
} from '@/lib/feed-settings-sections.ts';
import {
	dirtySectionIds,
	sectionErrorCounts,
	sectionPaths,
	SETTINGS_NOTE_KEY,
	validationKeys,
	validationTree
} from '@/lib/settings-sections.ts';
import { feedFieldError, feedUpgradeKey, feedWrites, normaliseTag } from '@/lib/settings-changes.ts';

/**
 * The tab a URL names, and the URL a tab wants.
 *
 * The seven settings tabs are still seven routes — a bookmarked Hashtags tab
 * has to keep working, and the back button has to keep walking the tabs — but
 * the redesigned shell selects by `activeSection` rather than by rendering an
 * `<Outlet/>`. So the path segment is translated rather than retired. Two names
 * differ because legacy's routes are singular where the section ids are not.
 */
const SECTION_BY_PATH = {
	detail: 'detail',
	update: 'updates',
	hashtags: 'hashtags',
	text: 'text',
	filter: 'filters',
	location: 'location',
	advanced: 'advanced'
};

const PATH_BY_SECTION = _.invert(SECTION_BY_PATH);

/** `status` and `medic` are not settings; they keep the legacy panel. */
const PANEL_PATHS = ['status', 'medic'];

/**
 * The API errors this screen reports. They describe the draft a save was
 * made from, so they are dropped with that draft: when the screen moves to
 * another input, and when it unmounts. Left in the store, they would show
 * on whichever input is opened next.
 */
const SAVE_ERROR_KEYS = ['INPUT_CREATE', 'INPUT_UPDATE', 'ROUTE_DELETE'];

class InputEdit extends React.Component {
	constructor(props) {
		super(props);

		let tab = 'settings',
			path = this.props.router.location.pathname;

		if (path.match(/status$/)) {
			tab = 'status';
		}

		this.state = {
			input: {},
			routeName: '',
			errors: null,
			currentTab: tab,
			credID: '',
			reauthClicked: false,
			fieldErrors: {},
			noteDismissed: false
		};

		this.save = this.save.bind(this);
		this.cancelAction = this.cancelAction.bind(this);
		this.deleteInput = this.deleteInput.bind(this);
		this.selectSection = this.selectSection.bind(this);
		this.editorPath = this.editorPath.bind(this);
		this.onFieldChange = this.onFieldChange.bind(this);
		this.reauthorizeInput = this.reauthorizeInput.bind(this);
		this.dismissNote = this.dismissNote.bind(this);

	}

	static getDerivedStateFromProps(props, state) {
		let nextState = {},
			input = null,
			defaultAttrs = {
				prefixText: '',
				prefixSpace: 0,
				postfixtText: '',
				postfixSpace: 0
			};

		if (props.app.firstLoad.ROUTE_LIST === true) {
			input = props.findInput(props.router.params.inputId, props);
			// if attrs are present we know we have the 'show' version of the input
			if (input !== null && input.attrs !== undefined) {
				if (_.isEmpty(state.input) || (parseInt(input.id, 10) !== parseInt(state.input.id, 10))) {
					nextState.input = Utilities.deepClone(input);
					// Refused-edit messages belong to the draft, so a new draft starts
					// without them. The screen stays mounted across inputs.
					nextState.fieldErrors = {};
					if (_.isArray(nextState.input.attrs)) {
						nextState.input.attrs = _.clone(defaultAttrs);
					}
				}
				if (state.credID !== '') {
					input.credID = state.credID;
					nextState.credID = '';
				}
			}
		}

		return (Object.keys(nextState).length) ? nextState : null;

	}

	componentDidMount() {
		let inputId = this.props.router.params.inputId,
			routeId = this.props.router.params.routeId,
			state = (this.props.router.query !== null && this.props.router.query.state) ? this.props.router.query.state : '';

		if (this.props.app.firstLoad.ROUTE_LIST === false) {
			this.props.getRoutes();
		}

		this.setState({noteDismissed: this.props.getPersistedData(this.noteKey()) === true});

		if (inputId && this.props.app.firstLoad.ROUTE_LIST === true) {
			this.props.getInput(inputId);
		} else if (!inputId && !routeId) {
			this.props.router.navigate("/share/automate");
		}

	}

	componentWillUnmount() {

		// relatedTags is global, so a search left open here would reappear on the
		// next source the reader opens. Legacy clears it from its own close
		// button; this clears it on the way out as well.
		this.props.clearRelatedHashtags();
		this.props.deleteApiError(SAVE_ERROR_KEYS);

	}

	componentDidUpdate(prevProps, prevState) {
		let errors = [],
			prevRoute = null,
			currentRoute = null,
			prevInput = null,
			currentInput = null;

		if (prevProps.app.firstLoad.ROUTE_LIST === false && this.props.app.firstLoad.ROUTE_LIST === true){
			this.props.getInput(this.props.router.params.inputId);
		}

		// Another input named in place, e.g. by the notification dropdown's "View
		// log" link. Until its record arrives the old draft is still in state;
		// draftIsCurrent keeps the screen loading meanwhile.
		if (prevProps.router.params.inputId !== this.props.router.params.inputId) {
			this.props.deleteApiError(SAVE_ERROR_KEYS);
			if (this.props.app.firstLoad.ROUTE_LIST === true) {
				this.props.getInput(this.props.router.params.inputId);
			}
		}

		// state.errors follows the store, so the banner goes when the error does:
		// on a retry, which clears it, as well as on the deletes above.
		errors = this.props.getApiErrors(SAVE_ERROR_KEYS);
		if (!errors.length && this.state.errors !== null) {
			this.setState({errors: null});
		} else if (errors.length && this.state.errors === null) {
			let input = this.state.input;
			if (errors[0].errors !== undefined && errors[0].errors.perms !== undefined) {
				this.props.showUpgradeModal(this.props.closeAllModals, null, errors[0], 'input_error');
				input = this.props.findInput(this.props.router.params.inputId);
				input = _.clone(input);
			}
			this.setState({
				errors: errors[0],
				input
			});
		}

		prevRoute = this.props.findRoute(this.props.router.params.routeId, prevProps);
		currentRoute = this.props.findRoute(this.props.router.params.routeId);

		// Input was deleted!
		if (prevRoute !== null && currentRoute !== null && _.isArray(prevRoute.inputs) && _.isArray(currentRoute.inputs)) {
			if (prevRoute.inputs.length > currentRoute.inputs.length) {
				this.props.router.navigate("/share/automate");
			}
		}

		if (prevProps.app.loading.INPUT_SHOW === false && this.props.app.loading.INPUT_SHOW === true) {
			if (window.location.href.match(/REAUTHINPUT/)) {
				this.props.authReturnHandler({
					path: '/share/automate/detail/' + this.props.router.params.routeId + '/input/' + this.props.router.params.inputId + '/detail'
				}, (input) => {
					this.props.resetAuthState();
					input.routeId = this.props.router.params.routeId;
					this.props.updateInput(input);
					this.setState({
						credID: input.credID
					});
				});
			}
		}

		if (prevProps.app.loading.INPUT_UPDATE === true && this.props.app.loading.INPUT_UPDATE === false && errors.length === 0) {
			this.props.router.navigate("/share/automate/detail/" + this.props.router.params.routeId);
		}

		if (prevProps.app.loading.ROUTE_DELETE === true && this.props.app.loading.ROUTE_DELETE === false && errors.length === 0) {
			this.props.router.navigate("/share/automate");
		}

	}

	/**
	 * Which note this screen shows, and so where its dismissal is remembered.
	 * The two screens this view can render carry different notes — basic's
	 * points down at destinations, complex's points up at the route as well —
	 * so dismissing one says nothing about the other.
	 */
	noteKey() {

		return (this.props.app.flags.routeViewType === this.props.app.config.constants.ROUTE_VIEW_ADVANCED)
			? SETTINGS_NOTE_KEY.source
			: SETTINGS_NOTE_KEY.automation;

	}

	dismissNote() {

		this.props.persistData(this.noteKey(), true);
		this.setState({noteDismissed: true});

	}

	/** `/share/automate/detail/:routeId/input/:inputId`, the prefix every tab hangs off. */
	editorPath() {

		return [
			'/share/automate/detail',
			this.props.router.params.routeId,
			'input',
			this.props.router.params.inputId
		].join('/');

	}

	/** Whether the draft in state is the input the URL names. */
	draftIsCurrent() {

		return parseInt(this.state.input.id, 10) === parseInt(this.props.router.params.inputId, 10);

	}

	/** Which settings tab the current URL names. */
	currentSection() {

		let segment = this.props.router.location.pathname.split('/').pop();

		return SECTION_BY_PATH[segment] || 'detail';

	}

	selectSection(id) {

		this.props.router.navigate([this.editorPath(), PATH_BY_SECTION[id] || 'detail'].join('/'));

	}

	deleteInput() {

		let confirmAction = () => {
				let constants = this.props.app.config.constants;

				if (this.state.input.id !== undefined) {
					// A simple account has one input per route, so deleting the input
					// is deleting the automation. InputEditorWrapper draws the same
					// distinction for the panel's own delete block.
					if (this.props.app.flags.routeViewType === constants.ROUTE_VIEW_ADVANCED) {
						this.props.deleteInput(this.state.input.id);
					} else {
						this.props.deleteRoute(this.props.router.params.routeId);
					}
				}
				this.props.closeAllModals();
			},
			deleteModal = (
				<DeleteModal
					modalCopy={"Are you sure you want to delete this feed?"}
					confirmAction={confirmAction}
					cancelAction={this.props.closeAllModals} />
			);

		this.props.addModal(deleteModal);

	}

	/**
	 * Every field-level edit on the settings screen.
	 *
	 * The screen reports what happened; `feedWrites` turns that into the dot
	 * paths and values the record stores, and each one goes through the same
	 * `FormUtilityMixin.requestChange` the legacy panels use. It mutates and
	 * re-sets `this.state` in place, so a change that writes two paths — echo's
	 * offsets and its prefixes — compounds correctly across the loop.
	 */
	onFieldChange(change) {

		if (change.type === 'action') {
			if (change.field.id === 'source-slot') {
				this.reauthorizeInput();
			}
			return;
		}

		// The related-hashtag search. Results land in state.utilities and come
		// back to the field as suggestions; neither variant stores anything.
		if (change.type === 'tag-search') {
			this.props.getRelatedHashtags(change.value);
			return;
		}

		if (change.type === 'tag-search-clear') {
			this.props.clearRelatedHashtags();
			return;
		}

		// A refused edit keeps its reason on the field until the next edit to
		// that field, which is how legacy's localErrors behave.
		let refusal = feedFieldError(change, this.state.input, this.props.app.config.constants);

		if (refusal) {
			this.setState({
				fieldErrors: Object.assign({}, this.state.fieldErrors, {[change.field.id]: refusal})
			});
			return;
		}

		if (this.state.fieldErrors[change.field.id]) {
			let cleared = Object.assign({}, this.state.fieldErrors);
			delete cleared[change.field.id];
			this.setState({fieldErrors: cleared});
		}

		let resources = (this.props.user.perms && this.props.user.perms.resources) ? this.props.user.perms.resources : {},
			upgrade = feedUpgradeKey(change, resources);

		// A gated control is still pressable; it opens the upgrade path. Some of
		// them also store — feedWrites decides that, not this.
		if (upgrade) {
			this.props.showUpgradeModal(this.props.closeAllModals, upgrade, null, 'input_edit');
		}

		feedWrites(change, this.state.input, {
			constants: this.props.app.config.constants,
			resources
		}).forEach((entry) => {
			FormUtilityMixin.requestChange.call(this, {
				currentTarget: {dataset: {field: entry.field}},
				target: {value: entry.value}
			});
		});

	}

	/**
	 * Send an OAuth input back through authorization. Ported from
	 * InputDetailPanel, which no longer renders: same guard against a double
	 * click, same X-is-down interception, same state string the return handler
	 * in componentDidUpdate matches on.
	 */
	reauthorizeInput() {

		let constants = this.props.app.config.constants,
			serviceId = this.state.input.type,
			url = "/remote/beginreauth?isID=:isId&iaID=:iaId&state=:state&final=:final",
			state = ['REAUTHINPUT', serviceId, this.state.input.id, new Date().getTime()].join("_");

		if (parseInt(serviceId, 10) === constants.INPUT_TYPE_TWITTER &&
			this.props.app.config.twitterDisabled !== undefined &&
			this.props.app.config.twitterDisabled === 1) {
			this.props.closeAllModals();
			this.props.addModal(
				<ConfirmModal
					modalCopy="Twitter is currently unavailable. We're working to resolve this issue."
					cancelAction={this.props.closeAllModals}
					showCancel={false}
					confirmAction={this.props.closeAllModals}
					confirmLabel={"OK"}/>
			);
			return;
		}

		if (this.state.reauthClicked) {
			return;
		}

		this.setState({reauthClicked: true}, () => {
			url = url.replace(':isId', serviceId);
			url = url.replace(':iaId', this.state.input.id);
			url = url.replace(':state', state);
			url = url.replace(':final', window.encodeURIComponent(this.props.cleanUrl(window.location.href)));
			window.location.assign(url);
		});

	}

	save() {
		let i = 0,
			schedsToSend = [],
			sched = {},
			rulesToSend = [],
			rule = '';

		// check for a new schedule which hasn't been set up
		// if no days have been selected, throw it away
		if (this.state.input.sched.length) {
			for (i = 0; i < this.state.input.sched.length; ++i) {
				sched = this.state.input.sched[i];
				if (sched.s_id.match !== undefined && sched.s_id.match('sch-') && sched.s_days === 0) {
					continue;
				}
				schedsToSend.push(sched);
			}
			this.state.input.sched = schedsToSend;
		}

		// clean up input rules. due to react assuming state is immutable and
		// rules being mutable, 'deleted' rules need to be hidden until the user hits 'save'
		if (this.state.input.rules.length) {
			for (i = 0; i < this.state.input.rules.length; ++i) {
				rule = this.state.input.rules[i];
				if (rule.deleted === undefined) {
					rulesToSend.push(rule);
				}
			}
			this.state.input.rules = rulesToSend;
		}

		// The error for required prefixes should already be showing, just ensure the
		// user can't save with no prefix
		if (this.state.input.attrs.repost && this.state.input.attrs.echoPrefix) {
			for (i = 0; i < this.state.input.attrs.repost.length; ++i) {
				if (this.state.input.attrs.echoPrefix[i] === undefined || this.state.input.attrs.echoPrefix[i] === '') {
					return;
				}
			}
		}

		this.props.updateInput(this.state.input);

	}

	cancelAction() {

		let routeId = this.props.router.params.routeId;

		this.props.router.navigate('/share/automate/detail/' + routeId);

	}

	render() {
		let constants = this.props.app.config.constants,
			EditHeader = null,
			MainPanel = null,
			title = (this.draftIsCurrent() && this.state.input.name) ? this.state.input.name : '',
			sectionTitle = "Your Feeds",
			currentTab = (this.props.router.location.pathname.match('medic')) ? 'medic' :
				(this.props.router.location.pathname.match('status')) ? 'status' : 'settings',
			{ children, ...otherProps } = this.props;


		if (parseInt(this.props.app.flags.routeViewType, 10) === constants.ROUTE_VIEW_ADVANCED) {
			let route = this.props.findRoute(this.props.router.params.routeId);
			if (route !== null) {
				sectionTitle = route.name;
			}
		}

		EditHeader = (
			<EditInputHeader
			    {...this.props}
				section={sectionTitle}
				action={title}
				routeViewType={this.props.app.flags.routeViewType}
				activeTab={currentTab}
				inputType={this.state.input.type} />
		);

		let cleanInput = this.props.findInput(this.props.router.params.inputId);

		if (PANEL_PATHS.indexOf(this.props.router.location.pathname.split('/').pop()) !== -1) {
			// Status and Feed Medic are reports rather than settings: no tab strip,
			// no save, no delete. They keep the legacy panel and its outlet context.
			otherProps.input = this.state.input;
			otherProps.cleanInput = cleanInput;
			otherProps.changeHandler = FormUtilityMixin.requestChange.bind(this);
			otherProps.statePath = "input";

			return (
				<Content
					header={EditHeader}
					main={<Outlet context={otherProps}/>} />
			);
		}

		MainPanel = this.renderSettings(cleanInput);

		return (
			<Content
				header={EditHeader}
				main={MainPanel} />
		);
	}

	/**
	 * THE TWO UIs GET TWO SCREENS OVER ONE SPEC.
	 *
	 * `sections` is built once and worn by either, because the difference is
	 * what the screens CONTAIN and where they sit, not how they look: in basic
	 * the source IS the automation, so the page is "Automation settings"; in
	 * complex the ROUTE is the automation and this is one source hanging off
	 * it, so the same title would be a lie. Sharing a title prop across that
	 * would hide the difference rather than express it.
	 *
	 * Both go back to the same place — the route detail — because that is what
	 * `cancelAction` has always done; only the label differs, and in complex it
	 * names the route the reader is returning to.
	 */
	renderSettings(cleanInput) {

		let constants = this.props.app.config.constants,
			input = this.state.input,
			// Nothing to save or delete until the draft is the input the URL names.
			loading = _.isEmpty(input) || input.attrs === undefined || !this.draftIsCurrent(),
			complex = this.props.app.flags.routeViewType === constants.ROUTE_VIEW_ADVANCED,
			route = this.props.findRoute(this.props.router.params.routeId),
			sections = buildFeedSections(input, cleanInput, {
				constants,
				resources: (this.props.user.perms && this.props.user.perms.resources) ? this.props.user.perms.resources : {},
				isFreeUser: this.props.isFreeUser(),
				fastPullUser: this.props.isAgencyUser() || this.props.isPlusUser() || this.props.isAgencyPlusUser(),
				routeViewType: this.props.app.flags.routeViewType,
				fieldErrors: this.state.fieldErrors,
				validation: validationTree(this.props.app),
				related: {
					// Normalised on the way in so a suggestion and a stored tag are
					// the same string — otherwise an already-added tag never reads
					// as picked. Legacy prefixes for the same comparison.
					tags: (this.props.utilities.relatedTags || []).map(normaliseTag),
					loading: this.props.app.loading.UTILITIES_FETCH_RELATED_HASHTAGS === true
				}
			}),
			paths = sectionPaths(sections, 'input', FEED_COMPOSITE_PATHS),
			editorPath = this.editorPath(),
			Screen = complex ? SourceSettings : FeedSettings,
			shared = {
				sections,
				source: feedSourceType(input, constants),
				subtype: feedSubtype(input, constants),
				sourceName: input.name || '',
				avatarUrl: input.icon || undefined,
				loading,
				formKey: String(input.id || ''),
				onChange: this.onFieldChange,
				activeSection: this.currentSection(),
				onSectionChange: this.selectSection,
				dirty: FormUtilityMixin.isObjectDirty(input, cleanInput),
				dirtySections: dirtySectionIds(paths, input, cleanInput),
				errorCounts: sectionErrorCounts(validationKeys(this.props.app), FEED_ERROR_KEYS),
				saving: this.props.app.loading.INPUT_UPDATE === true,
				formError: this.state.errors !== null
					? "We couldn't complete that update. Check the highlighted tabs for details."
					: undefined,
				onBack: this.cancelAction,
				/* `null` suppresses it; `undefined` lets the screen fill in its
				   own default text, which is what an undismissed note wants. */
				note: this.state.noteDismissed ? null : undefined,
				onDismissNote: this.dismissNote,
				actions: [
					{
						label: 'History',
						icon: <History/>,
						onClick: () => this.props.router.navigate(editorPath + '/status')
					},
					{
						label: 'Medic',
						icon: <Stethoscope/>,
						onClick: () => this.props.router.navigate(editorPath + '/medic')
					}
				],
				onSave: this.save,
				onCancel: this.cancelAction,
				onDelete: this.deleteInput
			};

		// The route's name is the back link and half the scope note. It is loaded
		// by the time the input is — getInput only runs once ROUTE_LIST is in —
		// so the fallback covers a render order rather than a real state, and it
		// is the noun complex uses rather than an empty chevron.
		if (complex) {
			shared.routeName = (route !== null && route.name) ? route.name : 'Route';
		} else {
			shared.backLabel = 'Automations';
		}

		return (
			<div className="tw-root">
				<Screen {...shared} />
			</div>
		);

	}

};

InputEdit.displayName = "InputEdit";

InputEdit.propTypes = {
	routeViewType: PropTypes.number,
	userPerms: PropTypes.object,
	recordPath: PropTypes.func,
	trialEligible: PropTypes.bool
};

InputEdit.defaultProps = {
	routeViewType: 0, // ROUTE_VIEW_ADVANCED
	userPerms: {},
	recordPath: () => {},
	trialEligible: false
};

const enhance = compose(
	ComponentWithContext,
	PersistData
);

export default enhance(InputEdit);
